import React, { useState, useEffect } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { PageLayout } from '../../../components/organisms/PageLayout';
import { api, setImpersonationToken, setImpersonationExpiresAt } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import '../../../styles/admin.css';

interface SubHistory {
  uid?: string;
  plan_name: string;
  plan_code: string;
  status: string;
  started_at: string;
  expires_at: string | null;
  cancelled_at: string | null;
}

interface Payment {
  payment_id: string | null;
  amount: number | null;
  currency: string;
  created_at: string | null;
  status: string | null;
}

interface UserDetail {
  uid: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  img_url: string | null;
  is_admin: boolean;
  is_banned: boolean;
  is_email_confirmed: boolean;
  created_at: string;
  plan_name: string;
  plan_code: string;
  subscription_status: string | null;
  subscription_expires_at: string | null;
  subscription_history: SubHistory[];
  cv_count: number;
  job_count: number;
  contact_count: number;
  ai_total_calls: number;
  ai_total_tokens_in: number;
  ai_total_tokens_out: number;
  ai_total_cost_usd: number;
}

const fmt = (dt: string | null) =>
  dt ? new Date(dt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const AdminUserDetail: React.FC = () => {
  const { uid } = useParams<{ uid: string }>();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [grantFormOpen, setGrantFormOpen] = useState(false);
  const [grantPlanUid, setGrantPlanUid] = useState('');
  const [grantDays, setGrantDays] = useState<number>(90);
  const [grantError, setGrantError] = useState('');
  const [plans, setPlans] = useState<{ uid: string; name: string; code: string }[]>([]);

  const loadUser = () => {
    if (!uid) return;
    Promise.all([
      api.get<UserDetail>(API_ENDPOINTS.admin.user(uid)),
      api.get<Payment[]>(API_ENDPOINTS.admin.userPayments(uid)),
    ])
      .then(([userDetail, paymentList]) => {
        setUser(userDetail);
        setPayments(paymentList);
      })
      .catch(() => {});
  };

  useEffect(() => {
    document.title = 'Admin | User Detail';
    if (!uid) return;
    Promise.all([
      api.get<UserDetail>(API_ENDPOINTS.admin.user(uid)),
      api.get<Payment[]>(API_ENDPOINTS.admin.userPayments(uid)),
      api.get<{ uid: string; name: string; code: string }[]>(API_ENDPOINTS.admin.plans),
    ])
      .then(([userDetail, paymentList, planList]) => {
        setUser(userDetail);
        setPayments(paymentList);
        const paid = planList.filter(p => p.code !== 'FREE_PLAN' && p.code !== 'TRIAL_PLAN');
        setPlans(paid);
        if (paid.length > 0) setGrantPlanUid(paid[0].uid);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [uid]);

  const handleToggleBan = async () => {
    if (!user) return;
    setActionLoading(true);
    try {
      await api.patch(API_ENDPOINTS.admin.updateUser(user.uid), { is_banned: !user.is_banned });
      setUser({ ...user, is_banned: !user.is_banned });
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleAdmin = async () => {
    if (!user) return;
    setActionLoading(true);
    try {
      await api.patch(API_ENDPOINTS.admin.updateUser(user.uid), { is_admin: !user.is_admin });
      setUser({ ...user, is_admin: !user.is_admin });
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const handleGrantSubscription = async () => {
    if (!user || !grantPlanUid) return;
    setGrantError('');
    setActionLoading(true);
    try {
      await api.post(API_ENDPOINTS.admin.grantSubscription(user.uid), {
        plan_uid: grantPlanUid,
        days: grantDays,
      });
      setGrantFormOpen(false);
      loadUser();
    } catch (e: any) {
      setGrantError(e?.detail || e?.message || 'Failed to grant subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeSubscription = async (subUid: string) => {
    if (!user) return;
    if (!window.confirm('Revoke this subscription immediately?')) return;
    setActionLoading(true);
    try {
      await api.delete(API_ENDPOINTS.admin.revokeSubscription(user.uid, subUid));
      loadUser();
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const handleImpersonate = async () => {
    if (!user) return;
    setActionLoading(true);
    try {
      const res = await api.post<{ access_token: string; expires_at: number }>(
        API_ENDPOINTS.admin.impersonate(user.uid), {}
      );
      setImpersonationToken(res.access_token);
      if (res.expires_at) setImpersonationExpiresAt(res.expires_at);
      window.location.href = '/welcome';
    } catch {
    } finally {
      setActionLoading(false);
    }
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      active: 'admin-badge--green',
      cancelling: 'admin-badge--orange',
      cancelled: 'admin-badge--gray',
      'cancelled by system': 'admin-badge--red',
      granted: 'admin-badge--blue',
      'cancelled by admin': 'admin-badge--gray',
    };
    return map[status] || 'admin-badge--gray';
  };

  return (
    <PageLayout breadcrumbItems={[{ label: 'Admin' }, { label: 'Users', href: '/admin/users' }, { label: user?.email || '…' }]}>
      <nav className="admin-nav">
        <NavLink to="/admin" end className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Overview</NavLink>
        <NavLink to="/admin/users" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Users</NavLink>
        <NavLink to="/admin/ai-usage" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>AI Usage</NavLink>
        <NavLink to="/admin/ai-settings" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>AI Settings</NavLink>
        <NavLink to="/admin/plans" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Plans</NavLink>
        <NavLink to="/admin/blog" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Blog</NavLink>
      </nav>

      <a href="/admin/users" className="admin-back">← Back to users</a>

      {loading && <div className="admin-loading">Loading…</div>}

      {user && (
        <>
          <div className="admin-btn-group" style={{ marginBottom: 16 }}>
            <button
              className={`admin-btn ${user.is_banned ? 'admin-btn--success' : 'admin-btn--danger'}`}
              onClick={handleToggleBan}
              disabled={actionLoading}
            >
              {user.is_banned ? 'Unban user' : 'Ban user'}
            </button>
            <button
              className={`admin-btn ${user.is_admin ? 'admin-btn--danger' : 'admin-btn--ghost'}`}
              onClick={handleToggleAdmin}
              disabled={actionLoading}
            >
              {user.is_admin ? 'Remove admin' : 'Make admin'}
            </button>
            <button
              className="admin-btn admin-btn--primary"
              onClick={handleImpersonate}
              disabled={actionLoading || user.is_banned}
            >
              Impersonate
            </button>
            <button
              className="admin-btn admin-btn--ghost"
              onClick={() => { setGrantFormOpen(v => !v); setGrantError(''); }}
              disabled={actionLoading}
            >
              {grantFormOpen ? 'Cancel grant' : 'Grant subscription'}
            </button>
          </div>

          {grantFormOpen && (
            <div className="admin-grant-form" style={{ marginBottom: 24, padding: '16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>Plan</label>
                <select
                  value={grantPlanUid}
                  onChange={e => setGrantPlanUid(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' }}
                >
                  {plans.map(p => (
                    <option key={p.uid} value={p.uid}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 }}>Duration</label>
                <select
                  value={grantDays}
                  onChange={e => setGrantDays(Number(e.target.value))}
                  style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' }}
                >
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={180}>180 days</option>
                  <option value={365}>365 days</option>
                </select>
              </div>
              <div>
                <button
                  className="admin-btn admin-btn--primary"
                  onClick={handleGrantSubscription}
                  disabled={actionLoading || !grantPlanUid}
                >
                  Confirm grant
                </button>
              </div>
              {grantError && (
                <div style={{ width: '100%', fontSize: 13, color: '#991b1b' }}>{grantError}</div>
              )}
            </div>
          )}

          <div className="admin-detail-grid">
            <div className="admin-detail-card">
              <div className="admin-detail-card__title">Profile</div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Email</span>
                <span className="admin-detail-row__value">{user.email}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Name</span>
                <span className="admin-detail-row__value">
                  {[user.first_name, user.last_name].filter(Boolean).join(' ') || '—'}
                </span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Registered</span>
                <span className="admin-detail-row__value">{fmt(user.created_at)}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Email confirmed</span>
                <span className="admin-detail-row__value">
                  {user.is_email_confirmed
                    ? <span className="admin-badge admin-badge--green">Yes</span>
                    : <span className="admin-badge admin-badge--gray">No</span>}
                </span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Admin</span>
                <span className="admin-detail-row__value">
                  {user.is_admin
                    ? <span className="admin-badge admin-badge--orange">Yes</span>
                    : <span className="admin-badge admin-badge--gray">No</span>}
                </span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Banned</span>
                <span className="admin-detail-row__value">
                  {user.is_banned
                    ? <span className="admin-badge admin-badge--red">Yes</span>
                    : <span className="admin-badge admin-badge--green">No</span>}
                </span>
              </div>
            </div>

            <div className="admin-detail-card">
              <div className="admin-detail-card__title">Subscription</div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Plan</span>
                <span className="admin-detail-row__value">{user.plan_name}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Status</span>
                <span className="admin-detail-row__value">
                  {user.subscription_status
                    ? <span className={`admin-badge ${statusBadge(user.subscription_status)}`}>{user.subscription_status}</span>
                    : '—'}
                </span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Expires</span>
                <span className="admin-detail-row__value">{fmt(user.subscription_expires_at)}</span>
              </div>
            </div>

            <div className="admin-detail-card">
              <div className="admin-detail-card__title">Content</div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">CVs</span>
                <span className="admin-detail-row__value">{user.cv_count}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Jobs</span>
                <span className="admin-detail-row__value">{user.job_count}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Contacts</span>
                <span className="admin-detail-row__value">{user.contact_count}</span>
              </div>
            </div>

            <div className="admin-detail-card">
              <div className="admin-detail-card__title">AI Usage (all time)</div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Total calls</span>
                <span className="admin-detail-row__value">{user.ai_total_calls}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Tokens in</span>
                <span className="admin-detail-row__value">{user.ai_total_tokens_in.toLocaleString()}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Tokens out</span>
                <span className="admin-detail-row__value">{user.ai_total_tokens_out.toLocaleString()}</span>
              </div>
              <div className="admin-detail-row">
                <span className="admin-detail-row__label">Total cost</span>
                <span className="admin-detail-row__value">${user.ai_total_cost_usd.toFixed(4)}</span>
              </div>
            </div>
          </div>

          {user.subscription_history.length > 0 && (
            <>
              <p className="admin-section-title">Subscription History</p>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Started</th>
                      <th>Expires</th>
                      <th>Cancelled</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {user.subscription_history.map((s, i) => (
                      <tr key={i}>
                        <td>{s.plan_name}</td>
                        <td><span className={`admin-badge ${statusBadge(s.status)}`}>{s.status}</span></td>
                        <td>{fmt(s.started_at)}</td>
                        <td>{fmt(s.expires_at)}</td>
                        <td>{fmt(s.cancelled_at)}</td>
                        <td>
                          {s.status === 'granted' && s.uid && (
                            <button
                              className="admin-btn admin-btn--danger"
                              style={{ padding: '3px 10px', fontSize: 12 }}
                              onClick={() => handleRevokeSubscription(s.uid!)}
                              disabled={actionLoading}
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <p className="admin-section-title">Payment History</p>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Payment ID</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 && (
                  <tr><td colSpan={4} className="admin-empty">No payments found</td></tr>
                )}
                {payments.map((p, i) => {
                  const pmtBadge = p.status === 'succeeded' ? 'admin-badge--green'
                    : p.status === 'failed' ? 'admin-badge--red'
                    : 'admin-badge--gray';
                  return (
                    <tr key={i}>
                      <td>{fmt(p.created_at)}</td>
                      <td>
                        {p.amount != null
                          ? `${p.amount.toFixed(2)} ${p.currency}`
                          : '—'}
                      </td>
                      <td>
                        {p.status
                          ? <span className={`admin-badge ${pmtBadge}`}>{p.status}</span>
                          : '—'}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11, color: '#6b7280' }}>
                        {p.payment_id || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageLayout>
  );
};

export default AdminUserDetail;
